import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SHARE_DURATION_OPTIONS } from './constants';
import CheckBox from '../../Checkbox/index';
import { useModules } from '../../../utils/modules';

/**
 * Modal component for sharing workkards.
 * Allows the user to select the duration and whether to share financial data.
 */
const ShareWorkkardModal = ({
  onClose,
  onConfirm,
  durationOptions = SHARE_DURATION_OPTIONS,
  selectedDurationId = SHARE_DURATION_OPTIONS[0].Id,
  showFinancial = false,
  shareFinancial = false,
}) => {
  const { isModuleEnabled } = useModules();
  const [durationId, setDurationId] = useState(selectedDurationId);
  // Default to sharing financials if duration is 'Always' (Id: 4) or passed as true —
  // never true when the module is off, regardless of duration/checkbox visibility.
  const [financial, setFinancial] = useState(
    isModuleEnabled("workkards.financial") && (shareFinancial || Number(selectedDurationId) === 4),
  );

  /**
   * Handles changes to the duration dropdown.
   * Disables financial sharing if duration is not 'Always' (4).
   * @param {React.ChangeEvent<HTMLSelectElement>} event
   */
  const handleDurationChange = (event) => {
    const nextDurationId = Number(event.target.value);
    setDurationId(nextDurationId);
    if (nextDurationId !== 4) {
      setFinancial(false);
    }
  };

  /**
   * Confirms the selection and calls the onConfirm callback.
   */
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm({
        selectedDurationId: durationId,
        shareFinancial: financial,
      });
    }
  };

  const isAlwaysDuration = durationId === 4;
  const shouldShowFinancialCheckbox = (showFinancial || isAlwaysDuration) && isModuleEnabled("workkards.financial");

  return (
    <React.Fragment>
      <div className="modalHeader">
        <h1>Share Workkards</h1>
      </div>

      <div className="modalBody">
        <p>
          Please specify the duration for which the workkards will be visible to selected organization.
        </p>

        <div className='formGroup'>
          <select
            value={durationId}
            onChange={handleDurationChange}
          >
            {durationOptions.map((option) => (
              <option key={option.Id} value={option.Id}>
                {option.Name}
              </option>
            ))}
          </select>
        </div>
        
        {shouldShowFinancialCheckbox && (
          <div>
            <CheckBox
              item={{
                id: 'share-financial',
                checked: financial,
                label: 'Share Financial'
              }}
              handleCheckbox={() => setFinancial((prev) => !prev)}
            />
          </div>
        )}
      </div>

      <div className="modalFooter">
        <button type="button" onClick={handleConfirm} className="btn-cc__primary" >
          Confirm
        </button>
        <button type="button" onClick={onClose} className="btn-cc__link" >
          Cancel
        </button>
      </div>
    </React.Fragment>
  );
};

ShareWorkkardModal.propTypes = {
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  durationOptions: PropTypes.array,
  selectedDurationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showFinancial: PropTypes.bool,
  shareFinancial: PropTypes.bool,
};

export default ShareWorkkardModal;
